import { useRef, useState } from 'react'
import {
  STAFF_CONNECTION_METHOD_VALUES,
  type TransferActionParameters,
} from '@super/shared-types'
import { useDirectory } from '@/contexts/directory.context'
import { Label } from '@/components/ui/label'
import {
  SelectableBox,
  SelectableBoxGroup,
} from '@/components/ui/selectable-box'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { MentionMultiSelect } from '@/components/actions/MentionMultiSelect'
import { editorRowToTransferAction } from '@/lib/handoff-rule.converters'
import { cn } from '@/lib/utils'
import type { RoleWithMembers, Staff } from '@/lib/api/directory.client'

const [SIMPLE_TRANSFER, AI_TRANSFER] = STAFF_CONNECTION_METHOD_VALUES

interface TransferActionInlineFormProps {
  value: TransferActionParameters
  onChange: (next: TransferActionParameters) => void
  /** Paints the Recipients field with the destructive border — set by the
   *  parent when `validateTransferParameters` returns an issue. */
  recipientsError?: boolean
}

export function TransferActionInlineForm({
  value,
  onChange,
  recipientsError,
}: TransferActionInlineFormProps) {
  const { roles, staff } = useDirectory()

  function patch(partial: Partial<TransferActionParameters>) {
    onChange({ ...value, ...partial })
  }

  const seedRef = useRef<string | null>(null)
  if (seedRef.current === null) {
    seedRef.current = recipientsToTargetString(value.recipients, roles, staff)
  }
  const [targetString, setTargetString] = useState<string>(seedRef.current)

  function handleTargetChange(nextTarget: string) {
    setTargetString(nextTarget)
    const api = editorRowToTransferAction(
      {
        id: 'inline',
        type: 'TRANSFER',
        target: nextTarget,
      },
      {
        connectionMethod: value.connectionMethod,
        preserveOriginalCallerId: value.preserveOriginalCallerId,
        declinedMessage: value.declinedMessage,
        transferMessage: value.transferMessage,
        voicemailDetectionEnabled: value.voicemailDetectionEnabled,
        leaveVoicemailEnabled: value.leaveVoicemailEnabled,
        voicemailMessage: value.voicemailMessage,
      },
      roles,
      staff,
    )
    const nextRecipients = (api.parameters as TransferActionParameters)
      .recipients
    onChange({ ...value, recipients: nextRecipients })
  }

  const voicemailLeaveDisabled = !value.voicemailDetectionEnabled
  const voicemailMessageDisabled =
    voicemailLeaveDisabled || !value.leaveVoicemailEnabled

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label className="px-3">Connection method</Label>
        <SelectableBoxGroup className="grid-cols-1">
          <SelectableBox
            id={SIMPLE_TRANSFER}
            selected={value.connectionMethod === SIMPLE_TRANSFER}
            onSelect={() => patch({ connectionMethod: SIMPLE_TRANSFER })}
          >
            <div className="space-y-0.5">
              <div className="font-medium">Simple transfer</div>
              <div
                className={cn(
                  'text-[11px]',
                  value.connectionMethod === SIMPLE_TRANSFER
                    ? 'text-white/80 dark:text-foreground/80'
                    : 'text-muted-foreground',
                )}
              >
                The agent conferences a live connection to a staff member.
              </div>
            </div>
          </SelectableBox>
          <SelectableBox
            id={AI_TRANSFER}
            selected={value.connectionMethod === AI_TRANSFER}
            onSelect={() => patch({ connectionMethod: AI_TRANSFER })}
          >
            <div className="space-y-0.5">
              <div className="font-medium">AI phone tree</div>
              <div
                className={cn(
                  'text-[11px]',
                  value.connectionMethod === AI_TRANSFER
                    ? 'text-white/80 dark:text-foreground/80'
                    : 'text-muted-foreground',
                )}
              >
                The agent briefs the recipient and asks for approval before
                connecting.
              </div>
            </div>
          </SelectableBox>
        </SelectableBoxGroup>
        {value.connectionMethod === SIMPLE_TRANSFER && (
          <>
            <hr className="my-3 border-border" />
            <label
              className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2.5 cursor-pointer hover:bg-accent/40"
              htmlFor="preserve-caller-id"
            >
              <div className="space-y-0.5">
                <p className="text-sm font-medium">
                  Preserve original caller id
                </p>
                <p className="text-xs text-muted-foreground">
                  Shows the caller&apos;s number instead of the agent number.
                </p>
              </div>
              <Switch
                id="preserve-caller-id"
                checked={value.preserveOriginalCallerId}
                onCheckedChange={next =>
                  patch({ preserveOriginalCallerId: next })
                }
              />
            </label>
          </>
        )}
      </div>

      <div className="space-y-2">
        <Label className="px-3">Recipients</Label>
        <MentionMultiSelect
          value={targetString}
          onChange={handleTargetChange}
          channel="phone"
          allowRepeats
          placeholder="Type @ to pick from directory or paste a phone number"
          hasError={recipientsError}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="transfer-message" className="px-3">
          Transfer message
        </Label>
        <Textarea
          id="transfer-message"
          value={value.transferMessage ?? ''}
          onChange={e => patch({ transferMessage: e.target.value || null })}
          placeholder="Line the agent says to the caller before it dials. Blank says nothing."
          rows={2}
        />
      </div>

      {value.connectionMethod === AI_TRANSFER && (
        <>
          <div className="space-y-2">
            <Label htmlFor="transfer-declined" className="px-3">
              Declined message
            </Label>
            <Textarea
              id="transfer-declined"
              value={value.declinedMessage ?? ''}
              onChange={e => patch({ declinedMessage: e.target.value || null })}
              placeholder="Line the agent reads when no recipient accepts."
              rows={2}
            />
          </div>

          <hr className="border-border" />

          <div className="space-y-4">
            <div>
              <Label className="px-3">Voicemail</Label>
              <p className="ml-3 text-xs text-muted-foreground">
                How the agent treats a machine that answers.
              </p>
            </div>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-0.5">
                <p className="px-3 text-sm font-medium">Detect voicemail</p>
                <p className="ml-3 text-xs text-muted-foreground">
                  Classify the recipient line and fire the voicemail outcome
                  instead of waiting out the briefing timeout.
                </p>
              </div>
              <Switch
                checked={value.voicemailDetectionEnabled}
                onCheckedChange={next =>
                  patch({ voicemailDetectionEnabled: next })
                }
              />
            </div>
            <div className="flex items-start justify-between gap-3">
              <div
                className={cn(
                  'space-y-0.5',
                  voicemailLeaveDisabled && 'opacity-50',
                )}
              >
                <p className="px-3 text-sm font-medium">Leave a message</p>
                <p className="ml-3 text-xs text-muted-foreground">
                  When a machine answers, play the message below at the beep.
                </p>
              </div>
              <Switch
                checked={value.leaveVoicemailEnabled}
                disabled={voicemailLeaveDisabled}
                onCheckedChange={next => patch({ leaveVoicemailEnabled: next })}
              />
            </div>
            <div
              className={cn(
                'space-y-1',
                voicemailMessageDisabled && 'opacity-50',
              )}
            >
              <Label htmlFor="voicemail-message" className="px-3">
                Voicemail message
              </Label>
              <Textarea
                id="voicemail-message"
                value={value.voicemailMessage ?? ''}
                disabled={voicemailMessageDisabled}
                onChange={e =>
                  patch({ voicemailMessage: e.target.value || null })
                }
                placeholder="Hi, this is {agent_name} with {company_name}. It looks like now isn't the best time to connect, so please give us a callback whenever you're ready!"
                rows={3}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export function recipientsToTargetString(
  recipients: TransferActionParameters['recipients'],
  roles: RoleWithMembers[],
  staff: Staff[],
): string {
  const pieces: string[] = []
  // A role chip covers one run of its members. A repeated member or any other
  // recipient ends the run, so a role listed twice reloads as two chips.
  let roleRun = null as { roleId: string; memberIds: Set<string> } | null
  for (const recipient of recipients) {
    const sourceRoleMemberId = recipient.sourceRoleMemberId
    if (sourceRoleMemberId) {
      const role = roles.find(r =>
        r.members.some(m => m.id === sourceRoleMemberId),
      )
      if (role) {
        if (
          roleRun?.roleId !== role.id ||
          roleRun.memberIds.has(sourceRoleMemberId)
        ) {
          roleRun = { roleId: role.id, memberIds: new Set() }
          pieces.push(`@${role.name}`)
        }
        roleRun.memberIds.add(sourceRoleMemberId)
        continue
      }
    }
    roleRun = null
    if (recipient.staffId) {
      const member = staff.find(s => s.id === recipient.staffId)
      if (member) {
        pieces.push(`@${member.name}`)
        continue
      }
    }
    pieces.push(
      recipient.extension
        ? `${recipient.number}#${recipient.extension}`
        : recipient.number,
    )
  }
  return pieces.join(', ')
}
